import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
// DeleteProgressModal — a per-step checklist for async session/project
// deletes. Modeled on the DeployModal in pages/ChatPage.tsx:2332 so the
// visual language is identical (✓ / spinner / ✗ / ○ per step, line-through
// on done, danger-red on failed). The server walks a fixed 7-step cleanup
// (cancel_agent → kill_processes → teardown_subprojects → rmtree_subdir →
// drop_checkpoint → clear_bus → drop_rows); for a project delete the list
// is 7 * N where N is the number of sessions.
//
// The sidebar removes the entry optimistically the moment the user
// confirms — this modal is purely the "what's happening on the server
// right now" view. If the job fails, the caller is responsible for
// re-adding the entry to the sidebar (we don't reach back into the
// sidebar store from here — that would couple this component to the
// specific store the caller is using).
import { useEffect, useRef, useState } from "react";
// Per-session step names in the same order as the server's
// _DELETE_STEP_NAMES (server/app.py). Used for the fallback label array
// before the first poll lands AND for displaying the per-step name on
// hover. Must stay in lockstep with the server.
const STEP_LABELS = {
    cancel_agent: "Cancelling agent",
    kill_processes: "Killing spawned processes",
    teardown_subprojects: "Tearing down sub-projects",
    rmtree_subdir: "Removing workspace files",
    drop_checkpoint: "Dropping agent checkpoint",
    clear_bus: "Clearing event bus",
    drop_rows: "Removing database rows",
};
const FALLBACK_LABELS = Object.values(STEP_LABELS);
export default function DeleteProgressModal({ open, targetKind, targetId, targetName, job, onPoll, onCancelJob, onClose, subtitle, }) {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const pollHandleRef = useRef(null);
    const jobIdRef = useRef(null);
    // Reset state when the modal opens with a new job.
    useEffect(() => {
        if (open && job) {
            jobIdRef.current = job.job_id;
            // Seed the status with the job's initial step list so the UI
            // renders immediately.
            setStatus({
                job_id: job.job_id,
                target_id: targetId,
                target_kind: targetKind,
                status: "pending",
                phase: "queued",
                steps: job.steps,
                error: null,
                created_at: Date.now() / 1000,
                updated_at: Date.now() / 1000,
                completed_at: null,
            });
            setError(null);
        }
        else if (!open) {
            jobIdRef.current = null;
            setStatus(null);
        }
    }, [open, job?.job_id, targetId, targetKind]);
    // Poll loop. 800ms cadence matches the deploy modal so the UI feels
    // consistent. Stops on any terminal status.
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        const tick = async () => {
            if (cancelled || !jobIdRef.current)
                return;
            try {
                const s = await onPoll();
                if (cancelled)
                    return;
                setStatus(s);
                if (s.status === "pending" || s.status === "running") {
                    pollHandleRef.current = setTimeout(tick, 800);
                }
            }
            catch (e) {
                if (cancelled)
                    return;
                setError(e?.message ?? "status poll failed");
                // Retry after a longer delay on error so we don't hot-loop.
                pollHandleRef.current = setTimeout(tick, 2500);
            }
        };
        // Kick off the first poll after a short delay so the modal can
        // paint the initial pending state first.
        pollHandleRef.current = setTimeout(tick, 200);
        return () => {
            cancelled = true;
            if (pollHandleRef.current)
                clearTimeout(pollHandleRef.current);
        };
    }, [open, onPoll]);
    if (!open)
        return null;
    const isRunning = status?.status === "pending" || status?.status === "running";
    const isTerminal = status?.status === "succeeded" || status?.status === "failed" || status?.status === "cancelled";
    const stepCount = status?.steps.length ?? job?.steps.length ?? 0;
    const onCancel = async () => {
        if (!onCancelJob || cancelling)
            return;
        setCancelling(true);
        try {
            await onCancelJob();
        }
        catch {
            // Best-effort. The next poll will see whatever state the worker
            // reached. Don't surface this error to the user — cancellation
            // is a "please stop" request, not a critical action.
        }
        finally {
            setCancelling(false);
        }
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4", role: "dialog", "aria-modal": "true", "data-testid": "delete-progress-modal", children: _jsxs("div", { className: "w-full max-w-lg rounded-lg border border-border bg-surface p-5 shadow-lg", children: [_jsxs("header", { className: "space-y-1", children: [_jsxs("h2", { className: "font-serif text-lg font-semibold text-text", children: ["Deleting ", targetKind, " ", _jsxs("span", { className: "font-mono text-base", children: ["\"", targetName, "\""] })] }), _jsxs("p", { className: "text-tx-xs text-muted", children: [isRunning
                                    ? `${status?.phase || "Starting…"} — sidebar entry has already been removed.`
                                    : isTerminal
                                        ? "Done. You can close this dialog."
                                        : subtitle || "Server-side teardown in progress.", stepCount > 0 && (_jsxs(_Fragment, { children: [" ", _jsxs("span", { className: "text-subtle", children: ["(", stepCount, " step", stepCount === 1 ? "" : "s", ")"] })] }))] })] }), _jsx("ol", { className: "mt-4 max-h-[60vh] space-y-1.5 overflow-y-auto pr-1", "data-testid": "delete-steps", children: (status?.steps ?? job?.steps ?? []).map((s, idx) => {
                        const status = s.status;
                        const isCurrent = status === "running";
                        const isDone_ = status === "done";
                        const isFailed_ = status === "failed";
                        // Fallback label: if the server hasn't populated the step
                        // name yet (the 202 response seeds them with the 7-cycle
                        // names but the order is a function of N sessions), use
                        // the cycle index.
                        const fallback = FALLBACK_LABELS[idx % FALLBACK_LABELS.length];
                        const label = s.label || STEP_LABELS[s.name] || fallback;
                        return (_jsxs("li", { className: "flex items-start gap-2 text-tx-sm", children: [_jsx("span", { className: "mt-0.5 inline-flex size-4 shrink-0 items-center justify-center font-mono " +
                                        (isDone_ ? "text-success" :
                                            isFailed_ ? "text-danger" :
                                                isCurrent ? "text-accent" :
                                                    "text-subtle"), "aria-hidden": true, children: isDone_ ? "✓" :
                                        isFailed_ ? "✗" :
                                            isCurrent ? (_jsx("span", { className: "inline-block size-3 animate-spin rounded-full border-2 border-accent border-t-transparent" })) : "○" }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: isCurrent ? "font-medium text-text" :
                                                isDone_ ? "text-muted line-through opacity-70" :
                                                    isFailed_ ? "text-danger" :
                                                        "text-subtle", children: label }), s.message && (_jsx("div", { className: "mt-0.5 text-tx-xs " + (isFailed_ ? "text-danger" : "text-muted"), children: s.message }))] })] }, idx));
                    }) }), error && (_jsx("p", { className: "mt-3 text-tx-xs text-danger", "data-testid": "delete-error", children: error })), status?.error && (_jsx("p", { className: "mt-3 text-tx-xs text-danger", "data-testid": "delete-step-error", children: status.error })), _jsxs("footer", { className: "mt-5 flex items-center justify-end gap-2", children: [isRunning && onCancelJob && (_jsx("button", { type: "button", onClick: onCancel, disabled: cancelling, className: "rounded-md border border-border px-3 py-1.5 text-tx-sm text-muted hover:border-danger/40 hover:text-danger disabled:opacity-50", "data-testid": "delete-cancel", children: cancelling ? "Cancelling…" : "Cancel" })), _jsx("button", { type: "button", onClick: onClose, className: "rounded-md bg-accent px-3 py-1.5 text-tx-sm font-medium text-on-accent hover:opacity-90", "data-testid": "delete-close", children: isRunning ? "Run in background" : isTerminal ? "Close" : "Close" })] })] }) }));
}
